import { useSyncExternalStore } from 'react';
import type { SheetItem } from '../../doc/schema';
import { IconBackward, IconDots, IconDuplicate, IconEye, IconEyeOff, IconForward, IconLock, IconMerge, IconPlus, IconTrash, IconUnlock } from '../../ui/icons';
import { useBoard, useBoardMeta, useItemsVersion, usePane, usePaneStore } from '../context';
import { PanelFrame } from '../floating';
import { rasterStore } from './rasterStore';
import { clearLayer, deleteLayer, duplicateLayer, enterPaint, exportSheetPng, flattenSheet, isSheet, mergeLayerDown, sheetLayerList } from './session';
import { sheetLabel } from './presets';
import { useEffect, useRef, useState } from 'react';

/**
 * The layer stack of one sketch sheet: the sheet being painted, else the selected sheet. Rows
 * are top-to-bottom in stacking order; the active row (the one strokes land on) is marked.
 */
export function SheetPanel({ onClose }: { onClose: () => void }) {
  const board = useBoard();
  const pane = usePaneStore();
  const meta = useBoardMeta();
  useItemsVersion();
  const store = rasterStore(board);
  useSyncExternalStore(store.subscribe, store.getVersion);
  const paintSheetId = usePane((s) => s.paintSheetId);
  const paintLayerId = usePane((s) => s.paintLayerId);
  const selection = usePane((s) => s.selection);
  const [menuFor, setMenuFor] = useState<string | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!menuFor) return;
    const close = (e: PointerEvent) => {
      if (!menuRef.current?.contains(e.target as Node)) setMenuFor(null);
    };
    window.addEventListener('pointerdown', close, true);
    return () => window.removeEventListener('pointerdown', close, true);
  }, [menuFor]);

  const [only] = selection;
  const sheetId = paintSheetId ?? (selection.size === 1 && only ? only : null);
  const sheet = sheetId ? board.index.get(sheetId) : undefined;
  const painting = !!paintSheetId && paintSheetId === sheetId;
  const activeId = painting ? paintLayerId : null;

  if (!isSheet(sheet)) {
    return (
      <PanelFrame kind="sheet" title="Sheet layers" className="sheet-panel" onClose={onClose}>
        <div className="insp-body">
          <div className="insp-muted">Select a sketch sheet (or double-click one to paint) to see its layers.</div>
        </div>
      </PanelFrame>
    );
  }

  const s = sheet as SheetItem;
  const rows = sheetLayerList(s).reverse(); // top first
  const ctx = { board, pane };
  const act = (fn: () => void) => () => {
    setMenuFor(null);
    fn();
  };

  const choose = (layerId: string) => {
    if (painting) pane.getState().setPaintLayer(layerId);
    else enterPaint(ctx, s.id, layerId);
  };

  const add = () => {
    const id = board.addSheetLayer(s.id, { aboveId: activeId ?? rows[0]?.[0] ?? null });
    if (id) choose(id);
  };

  const busy = store.busy(s.id);

  return (
    <PanelFrame kind="sheet" title="Sheet layers" className="sheet-panel" onClose={onClose}>
      <div className="sheet-panel-info" title={sheetLabel(s.props)}>
        {sheetLabel(s.props)}
        {busy && <span className="sheet-saving"> · saving…</span>}
      </div>
      <div className="layers-body">
        {rows.map(([id, def], idx) => {
          const active = id === activeId;
          const live = store.liveLayer(s.id, id);
          return (
            <div
              key={id}
              className={`layer-row sheet-layer-row${active ? ' is-active' : ''}${def.visible ? '' : ' is-hidden'}${def.locked ? ' is-locked' : ''}`}
              onClick={() => choose(id)}
            >
              <div className="layer-main">
                <button
                  type="button"
                  className="layer-toggle"
                  title={def.visible ? 'Hide layer' : 'Show layer'}
                  onClick={(e) => {
                    e.stopPropagation();
                    board.updateSheetLayer(s.id, id, { visible: !def.visible });
                  }}
                >
                  {def.visible ? <IconEye /> : <IconEyeOff />}
                </button>
                <button
                  type="button"
                  className="layer-toggle"
                  title={def.locked ? 'Unlock layer' : 'Lock layer (no painting on it)'}
                  onClick={(e) => {
                    e.stopPropagation();
                    board.updateSheetLayer(s.id, id, { locked: !def.locked });
                  }}
                >
                  {def.locked ? <IconLock /> : <IconUnlock />}
                </button>
                <button
                  type="button"
                  className="layer-name"
                  title="Click to paint on this layer; double-click to rename"
                  onDoubleClick={(e) => {
                    e.stopPropagation();
                    const n = prompt('Layer name', def.name);
                    if (n?.trim()) board.updateSheetLayer(s.id, id, { name: n.trim() });
                  }}
                >
                  {def.name}
                </button>
                {live && !live.retired && !live.loaded && <span className="layer-badge">{live.unavailable ? 'offline' : 'loading'}</span>}
                {!def.assetId && !live?.dirty && <span className="layer-count">empty</span>}
                <span className="layer-menu-host">
                  <button
                    type="button"
                    className="layer-toggle"
                    aria-label={`Options for ${def.name}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      setMenuFor(menuFor === id ? null : id);
                    }}
                  >
                    <IconDots />
                  </button>
                  {menuFor === id && (
                    <div className="menu layer-menu" ref={menuRef} role="menu" onClick={(e) => e.stopPropagation()}>
                      <button type="button" role="menuitem" disabled={idx === 0} onClick={act(() => board.moveSheetLayer(s.id, id, +1))}>
                        <span>Move up</span>
                      </button>
                      <button type="button" role="menuitem" disabled={idx === rows.length - 1} onClick={act(() => board.moveSheetLayer(s.id, id, -1))}>
                        <span>Move down</span>
                      </button>
                      <button type="button" role="menuitem" onClick={act(() => void duplicateLayer(board, s.id, id).then((nid) => nid && choose(nid)))}>
                        <span>Duplicate</span>
                      </button>
                      <button type="button" role="menuitem" disabled={idx === rows.length - 1} onClick={act(() => void mergeLayerDown(board, s.id, id))}>
                        <span>Merge down</span>
                      </button>
                      <button type="button" role="menuitem" disabled={!def.assetId && !live?.dirty} onClick={act(() => void clearLayer(board, s.id, id))}>
                        <span>Clear</span>
                      </button>
                      <div className="sep" />
                      <button
                        type="button"
                        role="menuitem"
                        className="danger"
                        onClick={act(() => {
                          if (rows.length === 1) {
                            if (confirm('This is the only layer: clear it?')) void clearLayer(board, s.id, id);
                          } else if (confirm(`Delete layer "${def.name}"?`)) void deleteLayer(board, s.id, id);
                        })}
                      >
                        <span>{rows.length === 1 ? 'Clear layer' : 'Delete layer'}</span>
                      </button>
                    </div>
                  )}
                </span>
              </div>
              {active && (
                <label className="sheet-opacity" onClick={(e) => e.stopPropagation()}>
                  <span>Opacity</span>
                  <input
                    type="range"
                    min={0}
                    max={100}
                    step={1}
                    value={Math.round(def.opacity * 100)}
                    onChange={(e) => board.updateSheetLayer(s.id, id, { opacity: Number(e.target.value) / 100 })}
                  />
                  <span className="opt-slider-value">{Math.round(def.opacity * 100)}%</span>
                </label>
              )}
            </div>
          );
        })}
      </div>
      <div className="layers-foot sheet-foot">
        <div className="btn-row">
          <button type="button" className="btn" title="New layer above the active one" onClick={add}>
            <IconPlus /> Layer
          </button>
          <button type="button" className="btn" title="Duplicate the active layer" disabled={!activeId} onClick={() => activeId && void duplicateLayer(board, s.id, activeId).then((nid) => nid && choose(nid))}>
            <IconDuplicate />
          </button>
          <button type="button" className="btn" title="Merge the active layer into the one below" disabled={!activeId || rows[rows.length - 1]?.[0] === activeId} onClick={() => activeId && void mergeLayerDown(board, s.id, activeId)}>
            <IconMerge />
          </button>
          <button type="button" className="btn" title="Move the active layer up" disabled={!activeId || rows[0]?.[0] === activeId} onClick={() => activeId && board.moveSheetLayer(s.id, activeId, +1)}>
            <IconForward />
          </button>
          <button type="button" className="btn" title="Move the active layer down" disabled={!activeId || rows[rows.length - 1]?.[0] === activeId} onClick={() => activeId && board.moveSheetLayer(s.id, activeId, -1)}>
            <IconBackward />
          </button>
          <button
            type="button"
            className="btn danger"
            title="Delete the active layer"
            disabled={!activeId}
            onClick={() => {
              if (!activeId) return;
              const def = s.props.layers[activeId];
              if (rows.length === 1) {
                if (confirm('This is the only layer: clear it?')) void clearLayer(board, s.id, activeId);
              } else if (confirm(`Delete layer "${def?.name ?? ''}"?`)) void deleteLayer(board, s.id, activeId);
            }}
          >
            <IconTrash />
          </button>
        </div>
        <div className="btn-row">
          {!painting && (
            <button type="button" className="btn" onClick={() => enterPaint(ctx, s.id)}>
              Paint
            </button>
          )}
          <button type="button" className="btn" title="An ordinary image of every visible layer, placed next to the sheet" onClick={() => void flattenSheet(ctx, s.id)}>
            Flatten to image
          </button>
          <button type="button" className="btn" title="Download the sheet as a PNG at full pixel size" onClick={() => void exportSheetPng(board, s.id, meta.title)}>
            Export PNG
          </button>
        </div>
      </div>
    </PanelFrame>
  );
}
